import React, { useState, useEffect } from 'react';
import { API_URL } from '../utils/config';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  MessageSquare,
  Send,
  Building2,
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  Home
} from 'lucide-react';


const PropertyReplyPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    fetchContext();
  }, [token]);

  const fetchContext = async () => {
    try {
      const res = await fetch(`${API_URL}/api/property-reply/${token}`);
      if (res.ok) {
        const data = await res.json();
        setContext(data);
      } else {
        setError('Link non valido o scaduto');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSending(true);
    setSendError(null);

    try {
      const res = await fetch(`${API_URL}/api/property-reply/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: replyContent })
      });

      const data = await res.json();

      if (res.ok) {
        setSent(true);
      } else {
        setSendError(data.detail || 'Errore nell\'invio del messaggio');
      }
    } catch (err) {
      setSendError('Errore di connessione');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Link Non Valido</h1>
          <p className="text-slate-500">{error}</p>
        </Card>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Messaggio Inviato!</h1>
          <p className="text-slate-500">
            La tua risposta è stata inviata al responsabile. 
            Riceverai una notifica via email se ci sarà una risposta.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">HW</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Holiday World</h1>
          <p className="text-slate-500">Sistema di Comunicazione</p>
        </div>

        {/* Property Info */}
        <Card className="p-4 mb-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-blue-600" />
            <div>
              <p className="font-semibold text-slate-800">{context?.property_name}</p>
              <p className="text-sm text-slate-500">{context?.subject}</p>
            </div>
          </div>
        </Card>

        {/* Recent Messages */}
        {context?.recent_messages?.length > 0 && (
          <Card className="p-4 mb-4">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Messaggi Recenti
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {context.recent_messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg ${
                    msg.direction === 'to_property'
                      ? 'bg-slate-100 ml-4'
                      : 'bg-green-50 mr-4'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {msg.direction === 'to_property' ? (
                      <>
                        <User className="h-3 w-3 text-slate-500" />
                        <span className="text-xs text-slate-500">Responsabile</span>
                      </>
                    ) : (
                      <>
                        <Home className="h-3 w-3 text-green-600" />
                        <span className="text-xs text-green-600">Tu (Proprietà)</span>
                      </>
                    )}
                    <span className="text-xs text-slate-400 ml-auto">
                      {new Date(msg.sent_at).toLocaleString('it-IT', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">{msg.content}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Reply Form */}
        <Card className="p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Send className="h-5 w-5 text-blue-600" />
            Rispondi al Responsabile
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Scrivi la tua risposta..."
                rows={5}
                className="w-full"
                required
              />
              <p className="text-xs text-slate-500 mt-2">
                Per la tua privacy, non includere informazioni personali come numeri di telefono, 
                email o indirizzi. Il sistema blocca automaticamente messaggi contenenti dati sensibili.
              </p>
            </div>

            {sendError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {sendError}
              </div>
            )}

            <Button
              type="submit"
              disabled={sending || !replyContent.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Invia Risposta
            </Button>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by HWsmart - Holiday World Smart Property Management
        </p>
      </div>
    </div>
  );
};

export default PropertyReplyPage;
